import { normalizeKbQuestionKey } from "../normalizeKbQuestionKey";
import type { KbAdminEditorSectionProps } from "../kbAdminEditorProps";

type Props = Pick<
  KbAdminEditorSectionProps,
  "aiQuestionAnswers" | "updateAiQuestionAnswer" | "setAiQuestionDisposition" | "appliedSuggestion"
>;

export function KbAiDetailsClarifyingQuestions(props: Props) {
  const { aiQuestionAnswers, updateAiQuestionAnswer, setAiQuestionDisposition, appliedSuggestion } = props;

  if (aiQuestionAnswers.length) {
    return (
      <div className="space-y-2 pt-2 border-t border-gray-100 dark:border-zinc-700">
        <div className="font-medium text-gray-900 dark:text-zinc-100">Clarifying questions</div>
        {aiQuestionAnswers.map((item) => {
          const answered = item.disposition === "answered" && !!item.answer.trim();
          return (
            <div key={normalizeKbQuestionKey(item.question)} className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-3 space-y-2 dark:border-zinc-700 dark:bg-zinc-900/45">
              <div className="flex items-center justify-between gap-3">
                <div className="text-gray-900 dark:text-zinc-100">{item.question}</div>
                <span
                  className={
                    "rounded-full px-2 py-0.5 text-[11px] border " +
                    (item.disposition === "different_document"
                      ? "border-sky-300 bg-sky-50 text-sky-800 dark:border-sky-700 dark:bg-sky-950/40 dark:text-sky-200"
                      : item.disposition === "not_applicable"
                        ? "border-gray-300 bg-gray-100 text-gray-700 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-300"
                        : answered
                          ? "border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200"
                          : "border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100")
                  }
                >
                  {item.disposition === "different_document"
                    ? "different doc"
                    : item.disposition === "not_applicable"
                      ? "not for this doc"
                      : answered
                        ? "answered"
                        : "needs answer"}
                </span>
              </div>
              <textarea
                className="h-20 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-zinc-600 dark:bg-zinc-900/60 dark:text-zinc-100"
                value={item.answer}
                onChange={(event) => updateAiQuestionAnswer(item.question, event.target.value)}
                placeholder={
                  item.disposition === "answered"
                    ? "Type the answer here. It will be sent back on the next AI processing run."
                    : "Optional note for why this question should be skipped or moved."
                }
              />
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  className="rounded-lg border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-100"
                  onClick={() => setAiQuestionDisposition(item.question, "answered")}
                >
                  Use as answer
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
                  onClick={() => setAiQuestionDisposition(item.question, "not_applicable")}
                >
                  Not for this doc
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-sky-300 bg-sky-50 px-3 py-2 text-sm text-sky-900 dark:border-sky-700 dark:bg-sky-950/40 dark:text-sky-100"
                  onClick={() => setAiQuestionDisposition(item.question, "different_document")}
                >
                  Belongs in another doc
                </button>
                <button
                  type="button"
                  className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-200"
                  onClick={() => setAiQuestionDisposition(item.question, "open")}
                >
                  Keep open
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  if (appliedSuggestion) {
    return (
      <div className="pt-1 border-t border-gray-100 dark:border-zinc-700 text-gray-500 dark:text-zinc-400">
        No clarifying questions returned for this pass.
      </div>
    );
  }

  return null;
}
